import { EXPERIMENTS, TRANSFERS } from '../lib/feedContext';
/**
 * Регуляторы человека едут за ним: банк, порог, курс, лимиты, темп, прокси контор.
 *
 * Направление важно и оно одно: при входе сервер побеждает локальное хранилище.
 * Локальное — это память браузера, серверное — память человека, а на чужой
 * машине он открывает сканер ровно затем, чтобы работать своими числами.
 *
 * Обратно уезжает с задержкой. Ползунок банка даёт десяток изменений за
 * секунду, и запрос на каждое из них — это лишние сто запросов за смену ради
 * величины, которую всё равно читают только при следующем входе.
 *
 * Отказ сервера здесь ничего не ломает: `localStorage` остаётся на месте, и
 * человек продолжает работать своими числами до восстановления связи.
 * Регуляторы — удобство, лента — работа.
 */

import { apiUrl } from '../lib/build';
import { applyProxies } from '../next/accounts';
import { useSettings } from '../store/settings';

/** Сколько ждём тишины перед отправкой. Ползунок за это время успевает встать. */
const QUIET_MS = 1200;

interface Wire {
  bank?: number;
  gate?: number;
  fx?: string;
  caps?: Record<string, number>;
  capsCur?: Record<string, 'RUB' | 'USD'>;
  freebets?: Record<string, number>;
  freebetsCur?: Record<string, 'RUB' | 'USD'>;
  attempts?: { seconds: number; navigation: number; at: number }[];
  taken?: number;
  tried?: number;
  proxies?: Record<string, string>;
}

function outgoing(): Wire {
  const state = useSettings.getState();
  return {
    bank: Math.round(state.bank),
    gate: Math.round(state.gate),
    // Курс уезжает строкой: деньги через число с плавающей точкой теряют
    // копейку молча, а на сервере он лежит как Decimal.
    fx: state.fx > 0 ? state.fx.toFixed(2) : undefined,
    caps: state.caps,
    capsCur: state.capsCur,
    freebets: state.freebets,
    freebetsCur: state.freebetsCur,
    attempts: state.attempts,
    taken: state.taken,
    tried: state.tried,
  };
}

/** Прочитать профиль с сервера и поставить его вместо локального. */
export async function pull(): Promise<boolean> {
  if (EXPERIMENTS) return false;
  try {
    const res = await fetch(apiUrl(TRANSFERS ? '/v1/profile?feed=transfers' : '/v1/profile'), { credentials: 'same-origin' });
    if (!res.ok) return false;
    const body = (await res.json()) as { profile?: Wire };
    const saved = body.profile ?? {};
    useSettings.getState().hydrate({
      ...saved,
      fx: saved.fx === undefined ? undefined : Number(saved.fx),
    });
    if (saved.proxies !== undefined && typeof saved.proxies === 'object' && saved.proxies !== null) {
      applyProxies(saved.proxies);
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Слать изменения регуляторов на сервер, пока страница открыта.
 *
 * Возвращает отписку. Первое сохранение не делается: подписка ставится сразу
 * после `pull`, и без этого страница отправляла бы серверу его же ответ.
 */
export function push(): () => void {
  if (EXPERIMENTS) return () => {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  const send = (): void => {
    void fetch(apiUrl(TRANSFERS ? '/v1/profile?feed=transfers' : '/v1/profile'), {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ profile: outgoing() }),
    }).catch(() => {
      // Молча. Настройки останутся в браузере и уедут при следующей правке.
    });
  };
  const unsubscribe = useSettings.subscribe(() => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(send, QUIET_MS);
  });
  return () => {
    if (timer !== null) clearTimeout(timer);
    unsubscribe();
  };
}
